import { useMutation } from '@apollo/client';
import { NotificationChannelType } from '@generated/graphql';
import type {
  NotificationPreference,
  UserNotificationPreference,
} from '@interfaces/organizationUser';
import { UserContext } from '@providers/userContextProvider';
import { Box, Switch, Typography, useTheme } from 'hephaestus/web';
import { isNil } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { SET_USER_NOTIFICATION_PREFERENCES } from './notificationPreferencesModal.query';

type PreferenceKey = keyof NotificationPreference;

export type NotificationPreferencesSettingsProp = {
  notificationPreference: UserNotificationPreference;
  notificationPreferences: UserNotificationPreference[];
  setNotificationPreferences: (value: UserNotificationPreference[]) => void;
};

export const NotificationPreferencesSettings = ({
  notificationPreference,
  notificationPreferences,
  setNotificationPreferences,
}: NotificationPreferencesSettingsProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('components');
  const theme = useTheme();
  const { userContext } = useContext(UserContext);

  const [setUserNotificationPreferences] = useMutation(
    SET_USER_NOTIFICATION_PREFERENCES,
  );

  const updateNotificationPreferences = (
    key: PreferenceKey,
    value: boolean,
  ) => {
    const notificationPreferencesInput: {
      notificationGroupId: string;
      preferences: NotificationPreference;
    }[] = [];
    const newNotificationPreferences = notificationPreferences.map((elem) => {
      let preferences: NotificationPreference = { ...elem.preferences };
      if (
        elem.notificationGroup.id ===
        notificationPreference.notificationGroup.id
      ) {
        preferences = { ...preferences, [key]: value };
      }
      notificationPreferencesInput.push({
        notificationGroupId: elem.notificationGroup.id,
        preferences,
      });
      return { ...elem, preferences };
    });
    setNotificationPreferences(newNotificationPreferences);
    if (!userContext.id) return;
    void setUserNotificationPreferences({
      variables: {
        input: {
          organizationUserId: userContext.id,
          userNotificationPreferences: notificationPreferencesInput,
        },
      },
    });
  };

  const disablePreferenceToggle = (channelType: NotificationChannelType) => {
    const value = (
      notificationPreference.notificationGroup?.notificationChannels ?? []
    ).find(
      (notificationChannel) => notificationChannel.type === channelType,
    )?.allowEditByClient;
    if (isNil(value)) return false;
    return !value;
  };

  if (!notificationPreference.notificationGroup) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box>
        <Typography variant='titleLarge'>
          {translateBackendString(
            notificationPreference.notificationGroup?.name,
          )}
        </Typography>
        {notificationPreference.notificationGroup?.description && (
          <Typography
            variant='bodyMedium'
            sx={{
              fontWeight: 400,
              color: theme.color.onSurfaceVariant,
            }}
          >
            {translateBackendString(
              notificationPreference.notificationGroup?.description,
            )}
          </Typography>
        )}
      </Box>
      {notificationPreference.notificationGroup.notificationChannels.some(
        (ch) => ch.type === NotificationChannelType.InApp && ch.value,
      ) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
            {t('notificationSettings.options.inApp')}
          </Typography>
          <Switch
            checked={notificationPreference.preferences?.enableInApp ?? false}
            disabled={disablePreferenceToggle(NotificationChannelType.InApp)}
            onChange={(e) =>
              updateNotificationPreferences('enableInApp', e.target.checked)
            }
          />
        </Box>
      )}
      {notificationPreference.notificationGroup.notificationChannels.some(
        (ch) => ch.type === NotificationChannelType.Push && ch.value,
      ) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
            {t('notificationSettings.options.push')}
          </Typography>
          <Switch
            checked={notificationPreference.preferences?.enablePush ?? false}
            disabled={disablePreferenceToggle(NotificationChannelType.Push)}
            onChange={(e) =>
              updateNotificationPreferences('enablePush', e.target.checked)
            }
          />
        </Box>
      )}
      {notificationPreference.notificationGroup.notificationChannels.some(
        (ch) => ch.type === NotificationChannelType.Email && ch.value,
      ) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
            {t('notificationSettings.options.email')}
          </Typography>
          <Switch
            checked={notificationPreference.preferences?.enableEmail ?? false}
            disabled={disablePreferenceToggle(NotificationChannelType.Email)}
            onChange={(e) =>
              updateNotificationPreferences('enableEmail', e.target.checked)
            }
          />
        </Box>
      )}
    </Box>
  );
};
